import { createTheme, alpha } from '@mui/material/styles';

/**
 * Millet color system — premium ink foundation with a creator-first blush accent
 * ───────────────────────────────────────────────────────────────────────────
 * Neutrals     Warm editorial paper and card surfaces
 * Soft surface Cool light gray (`surface.soft` / `surface.softHover`) for soft feature cards — not warm paper neutrals
 * Canvas       Lighter cool gray (`surface.canvas`) for the dashboard shell and sidebar
 * Ink          Primary CTAs, text hierarchy, structure
 * Blush        Creator emphasis, favorite states, expressive badges
 * Functional   Muted plum infrastructure — links, focus, selection, wayfinding
 * Semantic     Success / warning / danger + `live` urgency
 *
 * Legacy keys: `brand.olive` → functional plum; `brand.copper` / `brand.coral` → blush accent.
 */

/** @type {Record<string, string>} Canonical hex — design-system source */
export const TOKENS = {
  // Neutrals (warm)
  'neutral.0': '#FFFFFF',
  'neutral.50': '#FFFFFF',
  'neutral.100': '#F4EFE9',
  'neutral.200': '#E7E1DA',
  'neutral.300': '#D8D0C8',
  'neutral.400': '#B6ADA5',
  'neutral.500': '#8E857E',
  'neutral.600': '#6D6A67',
  'neutral.700': '#4A4746',
  'neutral.800': '#2A2A30',
  'neutral.900': '#19191D',
  // Soft cool gray — explore / feature cards (distinct from warm paper neutrals)
  'surface.soft': '#F3F3F3',
  'surface.softHover': '#E4E4E4',
  // Lighter cool gray — dashboard shell and sidebar
  'surface.canvas': '#FCFCFC',
  // Type — premium ink hierarchy.
  'text.primary': '#19191D',
  'text.secondary': '#6D6A67',
  'text.muted': '#817B76',
  'text.disabled': 'rgba(25, 25, 29, 0.38)',
  // Brand primary — ink ramp anchored at #19191D (contained CTAs / decisive actions)
  'brand.100': '#E8E6E4',
  'brand.200': '#D4D0CD',
  'brand.600': '#19191D',
  'brand.700': '#2A2A30',
  // Blush — creator-first accent anchored at #F57EB3.
  'blush.100': '#FDE6F1',
  'blush.200': '#FAD0E4',
  'blush.300': '#F8A9CD',
  'blush.500': '#F57EB3',
  'blush.600': '#E866A2',
  'blush.700': '#C94F87',
  'blush.800': '#9D3F6C',
  // Functional — muted plum infrastructure.
  'indigo.100': '#EAE4ED',
  'indigo.200': '#D6CADC',
  'indigo.600': '#5A5062',
  'indigo.700': '#453D4C',
  // Semantic
  'success.500': '#2E8B6F',
  'success.100': '#EAF5F1',
  'warning.500': '#D99A2B',
  'warning.100': '#FBF1DD',
  'danger.500': '#D84C4C',
  'danger.100': '#FAEAEA',
  'live.500': '#F57EB3',
  'live.100': '#FFF0F7',
  // Sand (secondary warm accent — rose-beige, `brand.caramel`)
  'sand.main': '#C9A89A',
  'sand.dark': '#A98072',
};

/** Canonical type roles for responsive product, dashboard, and marketing UI. */
export const FONT_SIZES = {
  heading: 'clamp(28px, 3.8vw, 48px)',
  hero: 'clamp(36px, 5.2vw, 64px)',
  body: 'clamp(15px, 1.2vw, 17px)',
  caption: 'clamp(11px, 0.9vw, 13px)',
  display: 'clamp(1.75rem, 2.4vw, 2rem)',
  title: 'clamp(1.125rem, 1.6vw, 1.35rem)',
  bodySm: '0.875rem',
  micro: '0.6875rem',
};

/** Canonical type emphasis; avoid weights heavier than strong in product UI. */
export const FONT_WEIGHTS = {
  regular: 400,
  medium: 500,
  strong: 600,
};

const NEUTRAL_HEX = {
  0: TOKENS['neutral.0'],
  50: TOKENS['neutral.50'],
  100: TOKENS['neutral.100'],
  200: TOKENS['neutral.200'],
  300: TOKENS['neutral.300'],
  400: TOKENS['neutral.400'],
  500: TOKENS['neutral.500'],
  600: TOKENS['neutral.600'],
  700: TOKENS['neutral.700'],
  800: TOKENS['neutral.800'],
  900: TOKENS['neutral.900'],
};

const BRAND_PRIMARY_HEX = {
  main: TOKENS['brand.600'],
  hover: TOKENS['brand.700'],
};

const BLUSH_HEX = {
  main: TOKENS['blush.500'],
  hover: TOKENS['blush.600'],
};

const INDIGO_HEX = {
  main: TOKENS['indigo.600'],
  hover: TOKENS['indigo.700'],
};

/** MUI `palette.text` + typography — same hue family as type tokens */
const TEXT = {
  primary: TOKENS['text.primary'],
  secondary: TOKENS['text.secondary'],
  muted: TOKENS['text.muted'],
  disabled: TOKENS['text.disabled'],
};

const brandPrimaryAlpha = (a) => alpha(BRAND_PRIMARY_HEX.main, a);
const blushAlpha = (a) => alpha(BLUSH_HEX.main, a);
const indigoAlpha = (a) => alpha(INDIGO_HEX.main, a);
/** Borders, dividers, overlays — keyed to text.primary RGB */
const inkAlpha = (a) => `rgba(25, 25, 29, ${a})`;

/** Hover/selection surfaces — nav, chips, filters, list rows (wayfinding, not blush) */
export const INTERACTION = {
  hover: inkAlpha(0.05),
  selected: indigoAlpha(0.08),
  selectedHover: indigoAlpha(0.12),
};

export const RADIUS_STANDARD = 10;
export const RADIUS_PILL = 9999;

const BRAND_PRIMARY = {
  main: BRAND_PRIMARY_HEX.main,
  hover: BRAND_PRIMARY_HEX.hover,
  light: brandPrimaryAlpha(0.08),
  contrastText: '#FFFFFF',
};

const FUNCTIONAL = {
  main: INDIGO_HEX.main,
  hover: INDIGO_HEX.hover,
  light: indigoAlpha(0.1),
  contrastText: '#FFFFFF',
};

/** Warm paper tints — neutral, not tied to the ink primary ramp */
const CREAM = {
  main: NEUTRAL_HEX[50],
  paper: TOKENS['neutral.0'],
  wash: TOKENS['neutral.100'],
};

/** Rose-beige sand — secondary warm accent (`brand.caramel`) */
const SAND = {
  main: TOKENS['sand.main'],
  dark: TOKENS['sand.dark'],
  light: alpha(TOKENS['sand.main'], 0.14),
  contrastText: NEUTRAL_HEX[900],
};

const WHITE = '#FFFFFF';

/** Creator-first blush accent; shared by legacy `brand.coral` and `brand.copper`. */
const brandBlushAccent = {
  main: BLUSH_HEX.main,
  dark: BLUSH_HEX.hover,
  light: blushAlpha(0.14),
  contrastText: BRAND_PRIMARY.main,
};

const NEUTRAL = {
  bg: NEUTRAL_HEX[50],
  surface: WHITE,
  border: TOKENS['neutral.200'],
  text: TEXT.primary,
  textSecondary: TEXT.secondary,
  textMuted: TEXT.muted,
};

/** MUI `primary` — premium ink (contained CTAs, decisive brand moments) */
const PRIMARY = {
  main: BRAND_PRIMARY.main,
  hover: BRAND_PRIMARY.hover,
  light: BRAND_PRIMARY.light,
};

const SUCCESS = {
  main: TOKENS['success.500'],
  dark: '#247058',
  light: TOKENS['success.100'],
  contrastText: '#FFFFFF',
};

const WARNING = {
  main: TOKENS['warning.500'],
  dark: '#B77F1F',
  light: TOKENS['warning.100'],
  contrastText: NEUTRAL_HEX[900],
};

const DANGER = {
  main: TOKENS['danger.500'],
  dark: '#B73C3C',
  light: TOKENS['danger.100'],
  contrastText: '#FFFFFF',
};

const LIVE = {
  main: TOKENS['live.500'],
  dark: TOKENS['blush.600'],
  light: TOKENS['live.100'],
  contrastText: BRAND_PRIMARY.main,
};

/**
 * Status badges — applications, invites, collaboration rows, campaign lifecycle.
 * Use `theme.palette.workflowStatus.*` or helpers in `utils/workflowStatusBadge.js`.
 */
const WORKFLOW_STATUS_BADGE = {
  /** Generic in-progress rows */
  positive: {
    bg: INTERACTION.selected,
    color: BRAND_PRIMARY.main,
  },
  /** Pending / submitted invites and applications */
  pending: {
    bg: TOKENS['blush.100'],
    color: TOKENS['blush.800'],
  },
  /** Accepted / approved — creator accent wash + blush label */
  committed: {
    bg: blushAlpha(0.14),
    color: BLUSH_HEX.main,
  },
  negotiating: {
    bg: BLUSH_HEX.main,
    color: TOKENS['neutral.0'],
  },
  negative: {
    bg: alpha(DANGER.main, 0.1),
    color: DANGER.main,
  },
  /** Same visuals as declines for canceled collaborations */
  canceled: {
    bg: alpha(DANGER.main, 0.1),
    color: DANGER.main,
  },
  neutral: {
    bg: NEUTRAL.surface,
    color: TEXT.secondary,
  },
  completed: {
    bg: FUNCTIONAL.light,
    color: FUNCTIONAL.main,
  },
};

const CAMPAIGN_LIFECYCLE_LIST = {
  active: WORKFLOW_STATUS_BADGE.positive,
  upcoming: {
    bg: WARNING.light,
    color: TEXT.primary,
  },
  archived: {
    bg: INTERACTION.selected,
    color: NEUTRAL_HEX[600],
  },
};

const CAMPAIGN_LIFECYCLE_DETAIL = {
  active: WORKFLOW_STATUS_BADGE.committed,
  upcoming: {
    bg: blushAlpha(0.14),
    color: BLUSH_HEX.main,
  },
  archived: {
    bg: alpha(NEUTRAL_HEX[600], 0.1),
    color: NEUTRAL_HEX[600],
  },
};

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
      /** Influencer detail: booking sidebar + desktop mosaic (751px) */
      influencerSidebar: 751,
      /** Influencer detail: expanded gallery height (909px) */
      influencerGallery: 909,
      /** Invite page: two-column layout (campaign list | creator preview) */
      inviteTwoColumn: 850,
      /** Brand profile reviews: two-column listing grid */
      reviewsTwoColumn: 700,
    },
  },
  palette: {
    grey: {
      50: NEUTRAL_HEX[50],
      100: NEUTRAL_HEX[100],
      200: NEUTRAL_HEX[200],
      300: NEUTRAL_HEX[300],
      400: NEUTRAL_HEX[400],
      500: NEUTRAL_HEX[500],
      600: NEUTRAL_HEX[600],
      700: NEUTRAL_HEX[700],
      800: NEUTRAL_HEX[800],
      900: NEUTRAL_HEX[900],
    },
    primary: {
      main: PRIMARY.main,
      light: PRIMARY.light,
      dark: PRIMARY.hover,
      contrastText: BRAND_PRIMARY.contrastText,
      hover: PRIMARY.hover,
    },
    secondary: {
      main: NEUTRAL_HEX[600],
      light: NEUTRAL_HEX[100],
      dark: NEUTRAL_HEX[800],
      contrastText: '#FFFFFF',
    },
    accent: {
      main: BLUSH_HEX.main,
      light: blushAlpha(0.14),
      contrastText: BRAND_PRIMARY.main,
    },
    functional: {
      main: FUNCTIONAL.main,
      dark: FUNCTIONAL.hover,
      light: FUNCTIONAL.light,
      contrastText: FUNCTIONAL.contrastText,
    },
    live: {
      main: LIVE.main,
      dark: LIVE.dark,
      light: LIVE.light,
      contrastText: LIVE.contrastText,
    },
    brand: {
      /** Blush — creator-first accent retained under the legacy coral key. */
      coral: brandBlushAccent,
      functional: {
        main: FUNCTIONAL.main,
        dark: FUNCTIONAL.hover,
        light: FUNCTIONAL.light,
        contrastText: FUNCTIONAL.contrastText,
      },
      /** @deprecated Use `brand.functional` — maps to infrastructure plum */
      olive: {
        main: FUNCTIONAL.main,
        dark: FUNCTIONAL.hover,
        light: FUNCTIONAL.light,
        contrastText: FUNCTIONAL.contrastText,
      },
      /** @deprecated Use `palette.text` / TEXT — warm plum ink hierarchy */
      forest: {
        main: TEXT.primary,
        muted: TEXT.secondary,
        soft: TEXT.muted,
        contrastText: WHITE,
      },
      cream: {
        main: CREAM.main,
        paper: CREAM.paper,
        wash: CREAM.wash,
      },
      caramel: {
        main: SAND.main,
        dark: SAND.dark,
        light: SAND.light,
        contrastText: SAND.contrastText,
      },
      /** @deprecated Use `brand.coral` / `palette.accent` */
      copper: brandBlushAccent,
    },
    cta: {
      main: BRAND_PRIMARY.main,
      dark: BRAND_PRIMARY.hover,
      light: BRAND_PRIMARY.light,
      contrastText: BRAND_PRIMARY.contrastText,
    },
    /** Favorites / hearts — blush creator accent, separate from primary CTAs */
    favorite: {
      main: BLUSH_HEX.main,
      dark: BLUSH_HEX.hover,
      light: blushAlpha(0.12),
      contrastText: BRAND_PRIMARY.main,
    },
    success: {
      main: SUCCESS.main,
      dark: SUCCESS.dark,
      light: SUCCESS.light,
      contrastText: SUCCESS.contrastText,
    },
    warning: {
      main: WARNING.main,
      dark: WARNING.dark,
      light: WARNING.light,
      contrastText: WARNING.contrastText,
    },
    error: {
      main: DANGER.main,
      dark: DANGER.dark,
      light: DANGER.light,
      contrastText: DANGER.contrastText,
    },
    info: {
      main: FUNCTIONAL.main,
      dark: FUNCTIONAL.hover,
      light: FUNCTIONAL.light,
      contrastText: FUNCTIONAL.contrastText,
    },
    neutral: {
      main: TEXT.primary,
      hover: TEXT.secondary,
      contrastText: WHITE,
    },
    applied: {
      main: BLUSH_HEX.main,
      light: INTERACTION.selected,
      dark: BLUSH_HEX.hover,
      contrastText: BRAND_PRIMARY.main,
      outline: `2px solid ${BLUSH_HEX.main}`,
      glow: `0 1px 4px ${blushAlpha(0.14)}`,
      glowHover: `0 2px 8px ${blushAlpha(0.18)}`,
    },
    background: {
      default: NEUTRAL.bg,
      paper: NEUTRAL.surface,
      surface: NEUTRAL.surface,
      soft: TOKENS['surface.soft'],
      softHover: TOKENS['surface.softHover'],
      canvas: TOKENS['surface.canvas'],
      dark: NEUTRAL_HEX[900],
    },
    text: {
      primary: TEXT.primary,
      secondary: TEXT.secondary,
      disabled: TEXT.disabled,
      muted: TEXT.muted,
    },
    divider: NEUTRAL.border,
    border: {
      subtle: NEUTRAL.border,
    },
    highlight: {
      tint: INTERACTION.selected,
    },
    workflowStatus: {
      positive: { ...WORKFLOW_STATUS_BADGE.positive },
      pending: { ...WORKFLOW_STATUS_BADGE.pending },
      committed: { ...WORKFLOW_STATUS_BADGE.committed },
      negotiating: { ...WORKFLOW_STATUS_BADGE.negotiating },
      negative: { ...WORKFLOW_STATUS_BADGE.negative },
      canceled: { ...WORKFLOW_STATUS_BADGE.canceled },
      neutral: { ...WORKFLOW_STATUS_BADGE.neutral },
      completed: { ...WORKFLOW_STATUS_BADGE.completed },
      campaign: {
        lifecycle: {
          active: { ...CAMPAIGN_LIFECYCLE_LIST.active },
          upcoming: { ...CAMPAIGN_LIFECYCLE_LIST.upcoming },
          archived: { ...CAMPAIGN_LIFECYCLE_LIST.archived },
        },
        detail: {
          active: { ...CAMPAIGN_LIFECYCLE_DETAIL.active },
          upcoming: { ...CAMPAIGN_LIFECYCLE_DETAIL.upcoming },
          archived: { ...CAMPAIGN_LIFECYCLE_DETAIL.archived },
        },
      },
    },
    decorative: {
      surfaceWash: inkAlpha(0.025),
    },
    chat: {
      threadBackground: NEUTRAL.surface,
      threadBackgroundDark: NEUTRAL_HEX[900],
      surfaceMuted: WHITE,
      surfaceSubtle: TOKENS['surface.soft'],
      dateSeparator: TOKENS['surface.soft'],
      surfaceMutedDark: 'rgba(255, 255, 255, 0.04)',
      dateSeparatorDark: 'rgba(255, 255, 255, 0.08)',
      composerFieldDark: 'rgba(255, 255, 255, 0.06)',
      filterTrackDark: 'rgba(255, 255, 255, 0.06)',
      chipNeutralBgDark: 'rgba(255, 255, 255, 0.06)',
      chipNeutralBorderDark: 'rgba(255, 255, 255, 0.12)',
      windowShadow: '0 12px 40px rgba(25, 25, 29, 0.08)',
      softShadow: '0 1px 2px rgba(25, 25, 29, 0.04)',
      softerShadow: '0 1px 1px rgba(25, 25, 29, 0.03)',
      selectedRowShadow: '0 1px 3px rgba(25, 25, 29, 0.06)',
      filterActiveShadow: `0 1px 3px ${indigoAlpha(0.12)}`,
    },
    /** Header search — Safari-like glass + Millet blush focus */
    searchChrome: {
      glass: alpha(NEUTRAL.surface, 0.86),
      glow: `0 16px 48px ${inkAlpha(0.1)}`,
    },
    action: {
      active: inkAlpha(0.54),
      hover: INTERACTION.hover,
      selected: INTERACTION.selected,
      selectedHover: INTERACTION.selectedHover,
      disabled: inkAlpha(0.28),
      disabledBackground: inkAlpha(0.06),
    },
  },
  radius: {
    standard: RADIUS_STANDARD,
    pill: RADIUS_PILL,
  },
  shape: {
    borderRadius: RADIUS_STANDARD,
  },
  typography: {
    fontFamily: '"Inter", "Poppins", "system-ui", sans-serif',
    editorial: {
      fontFamily: '"Georgia", "Times New Roman", serif',
    },
    h1: {
      fontFamily: '"Inter", "Poppins", "system-ui", sans-serif',
      fontSize: '3rem',
      fontWeight: 700,
      color: NEUTRAL_HEX[900],
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: '"Inter", "Poppins", "system-ui", sans-serif',
      fontSize: '2.5rem',
      fontWeight: 600,
      color: NEUTRAL_HEX[900],
      letterSpacing: '-0.02em',
    },
    h3: {
      fontFamily: '"Inter", "Poppins", "system-ui", sans-serif',
      fontSize: '2rem',
      fontWeight: 600,
      color: TEXT.primary,
    },
    h4: {
      fontFamily: '"Inter", "Poppins", "system-ui", sans-serif',
      fontSize: '1.75rem',
      fontWeight: 600,
      color: TEXT.primary,
    },
    h5: {
      fontFamily: '"Inter", "Poppins", "system-ui", sans-serif',
      fontSize: '1.5rem',
      fontWeight: 600,
      color: TEXT.primary,
    },
    h6: {
      fontFamily: '"Inter", "Poppins", "system-ui", sans-serif',
      fontSize: '1.25rem',
      fontWeight: 600,
      color: TEXT.primary,
    },
    body1: {
      fontFamily: '"Inter", "Poppins", "system-ui", sans-serif',
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.6,
      color: TEXT.primary,
    },
    body2: {
      fontFamily: '"Inter", "Poppins", "system-ui", sans-serif',
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.55,
      color: TEXT.secondary,
    },
    subtitle1: {
      fontFamily: '"Inter", "Poppins", "system-ui", sans-serif',
      fontSize: '1rem',
      fontWeight: 500,
      color: TEXT.primary,
    },
    subtitle2: {
      fontFamily: '"Inter", "Poppins", "system-ui", sans-serif',
      fontSize: '0.875rem',
      fontWeight: 500,
      color: TEXT.secondary,
    },
    caption: {
      fontFamily: '"Inter", "Poppins", "system-ui", sans-serif',
      fontSize: '0.75rem',
      fontWeight: 400,
      color: TEXT.muted,
    },
    overline: {
      fontFamily: '"Inter", "Poppins", "system-ui", sans-serif',
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      color: TEXT.secondary,
    },
    button: {
      textTransform: 'none',
      fontFamily: '"Inter", "Poppins", "system-ui", sans-serif',
      fontWeight: 600,
    },
  },
  components: {
    MuiCollapse: {
      defaultProps: {
        timeout: 200,
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: NEUTRAL.bg,
          color: TEXT.primary,
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: '"Inter", "Poppins", "system-ui", sans-serif',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme: muiTheme }) => ({
          borderRadius: muiTheme.shape.borderRadius,
          padding: '10px 22px',
          textTransform: 'none',
          fontWeight: 600,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        }),
        contained: {
          backgroundColor: PRIMARY.main,
          color: BRAND_PRIMARY.contrastText,
          '&:hover': {
            backgroundColor: PRIMARY.hover,
          },
        },
        containedPrimary: {
          backgroundColor: PRIMARY.main,
          color: BRAND_PRIMARY.contrastText,
          '&:hover': {
            backgroundColor: PRIMARY.hover,
          },
        },
        outlined: {
          borderColor: NEUTRAL.border,
          color: TEXT.primary,
          '&:hover': {
            borderColor: inkAlpha(0.22),
            backgroundColor: inkAlpha(0.02),
          },
        },
        text: {
          color: FUNCTIONAL.main,
          '&:hover': {
            backgroundColor: indigoAlpha(0.05),
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.2s ease, color 0.2s ease, transform 0.2s ease',
          '&:hover': {
            backgroundColor: inkAlpha(0.05),
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme: muiTheme }) => ({
          borderRadius: muiTheme.shape.borderRadius,
          padding: 24,
          backgroundImage: 'none',
          boxShadow: '0 1px 0 rgba(25, 25, 29, 0.06)',
        }),
        outlined: {
          borderColor: NEUTRAL.border,
          boxShadow: 'none',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: ({ theme: muiTheme }) => ({
          '& .MuiOutlinedInput-root': {
            borderRadius: muiTheme.shape.borderRadius,
            backgroundColor: NEUTRAL.surface,
            '& fieldset': {
              borderColor: NEUTRAL.border,
            },
            '&:hover fieldset': {
              borderColor: NEUTRAL.textMuted,
            },
            '&.Mui-focused fieldset': {
              borderColor: FUNCTIONAL.main,
              borderWidth: '1px',
            },
            '& input': {
              '&::placeholder': {
                color: NEUTRAL.textMuted,
                opacity: 1,
              },
            },
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme: muiTheme }) => ({
          borderRadius: muiTheme.shape.borderRadius,
        }),
        notchedOutline: {
          borderColor: NEUTRAL.border,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundColor: NEUTRAL.surface,
          color: TEXT.primary,
          boxShadow: `0 1px 0 ${NEUTRAL.border}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          borderRight: `1px solid ${NEUTRAL.border}`,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: NEUTRAL_HEX[100],
          color: TEXT.secondary,
          border: '1px solid transparent',
          fontWeight: 500,
          fontSize: FONT_SIZES.bodySm,
          height: 32,
          borderRadius: RADIUS_PILL,
          paddingLeft: 12,
          paddingRight: 12,
          fontFamily: '"Inter", "Poppins", "system-ui", sans-serif',
        },
        outlined: {
          backgroundColor: NEUTRAL.surface,
          color: TEXT.secondary,
          border: `1px solid ${NEUTRAL.border}`,
        },
        /** Do not paint default filled styles over `color="primary"` (and other colors). */
        filled: ({ ownerState }) => {
          if (ownerState.color && ownerState.color !== 'default') {
            return {};
          }
          return {
            backgroundColor: NEUTRAL_HEX[100],
            color: TEXT.secondary,
          };
        },
        colorPrimary: {
          backgroundColor: PRIMARY.main,
          color: BRAND_PRIMARY.contrastText,
          border: '1px solid transparent',
          '&:hover': {
            backgroundColor: PRIMARY.hover,
          },
        },
        colorSecondary: {
          backgroundColor: NEUTRAL.textSecondary,
          color: '#FFFFFF',
          border: '1px solid transparent',
        },
        colorSuccess: {
          backgroundColor: SUCCESS.main,
          color: SUCCESS.contrastText,
          border: '1px solid transparent',
        },
        colorWarning: {
          backgroundColor: WARNING.main,
          color: WARNING.contrastText,
          border: '1px solid transparent',
        },
        colorError: {
          backgroundColor: DANGER.main,
          color: DANGER.contrastText,
          border: '1px solid transparent',
        },
        colorInfo: {
          backgroundColor: FUNCTIONAL.main,
          color: FUNCTIONAL.contrastText,
          border: '1px solid transparent',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: FUNCTIONAL.main,
          fontWeight: 500,
          '&:hover': {
            color: FUNCTIONAL.hover,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          color: TEXT.secondary,
          '&.Mui-selected': {
            color: FUNCTIONAL.main,
            fontWeight: 600,
          },
        },
      },
    },
  },
});

export { NEUTRAL, PRIMARY, TEXT };
export default theme;
